import React, { useState, useEffect } from "react";
import Addons from "../Utils/Addons";
import CTAPopup from "./CTAPopup";
import CheckoutDetails from "./CheckoutDetails";
import { ReactComponent as Arrow } from "../../assets/images/long-arrow-right.svg";

const CheckOut = ({
  user = {},
  id = "",
  event = {},
  tickets = {},
  expiry = null,
  selectedAddons = {},
  setSelectedAddons = () => {},
  subtotal = 0,
  setSubtotal = () => {},
  showReceipt = false,
  setShowReceipt = () => {},
  grandTotal = 0,
  setGrandTotal = () => {},
  nextStep = () => {},
  cancelOrder = () => {},
  tempCart = {},
  setTempCart = () => {},
  addons = [],
  ticketOptions = [],
  handleStepClick = () => {},
  editGuests = false,
  currency,
  commission = 0,
}) => {
  const [totalTickets, setTotalTickets] = useState(0);

  useEffect(() => {
    getCartTotal();
  }, [tickets, tempCart, selectedAddons]);

  // Reconcile the selection with the cart, which the buyer can still go back
  // and change. A bulk add-on's count used to be frozen at whatever the total
  // was when they ticked it, and a per-unit add-on assigned to ticket 4 would
  // otherwise keep pointing at a ticket that no longer exists.
  useEffect(() => {
    const seats = ticketOptions.length;
    if (!seats) return;
    let changed = false;
    const current = { ...selectedAddons };

    for (const id of Object.keys(current)) {
      const selection = current[id];
      if (selection.type === "bulk") {
        if (selection.count !== seats) {
          current[id] = { ...selection, count: seats };
          changed = true;
        }
        continue;
      }
      if (selection.type !== "per unit") continue;

      const inRange = (selection.ticketIndices || []).filter(
        (index) => index < seats
      );
      if (inRange.length === (selection.ticketIndices || []).length) continue;
      changed = true;
      if (inRange.length === 0) {
        delete current[id];
      } else {
        current[id] = {
          ...selection,
          ticketIndices: inRange,
          count: inRange.length,
        };
      }
    }

    if (changed) setSelectedAddons(current);
  }, [ticketOptions.length, selectedAddons]);

  const getCartTotal = () => {
    let sum = 0;
    let price = 0;
    let cart = event.type === "full" ? { ...tempCart } : { ...tickets };
    for (const ticket in cart) {
      sum += cart[ticket].quantity;
      price += cart[ticket].quantity * cart[ticket].price;
    }
    for (const addon in selectedAddons) {
      price += selectedAddons[addon].price * selectedAddons[addon].count;
    }
    setTotalTickets(sum);
    setSubtotal(price);
  };

  const handleAddonChange = (addon) => {
    const current = { ...selectedAddons };
    if (current[addon.id]) {
      delete current[addon.id];
      setSelectedAddons(current);
      return;
    }
    // A per-unit add-on lands on the first ticket, so picking one always costs
    // something the moment it is ticked. The buyer moves or extends it from
    // there; clearing every ticket removes the add-on again.
    const ticketIndices =
      addon.type === "per unit" && ticketOptions.length > 0 ? [0] : [];
    let count = 1;
    if (addon.type === "bulk") {
      count = ticketOptions.length || totalTickets;
    }
    setSelectedAddons({
      ...current,
      [addon.id]: {
        count,
        price: addon.price,
        name: addon.name,
        type: addon.type,
        ticketIndices,
      },
    });
  };

  // Only reachable where there are no tickets to assign to -- see showStepper
  // in Addons. Everywhere else the assignment IS the quantity.
  const handleAddonCountChange = (addon, direction) => {
    const current = { ...selectedAddons };
    const selection = current[addon.id];
    if (!selection) return;

    const count = selection.count + (direction === "add" ? 1 : -1);
    if (count <= 0) {
      delete current[addon.id];
    } else {
      current[addon.id] = { ...selection, count };
    }
    setSelectedAddons(current);
  };

  const handleTicketAssignment = (addon, ticketIndex) => {
    const current = { ...selectedAddons };
    const selection = current[addon.id];
    if (!selection) return;

    const assigned = selection.ticketIndices || [];
    const next = assigned.includes(ticketIndex)
      ? assigned.filter((index) => index !== ticketIndex)
      : [...assigned, ticketIndex].sort((a, b) => a - b);

    if (next.length === 0) {
      // An add-on covering no tickets is not an add-on.
      delete current[addon.id];
    } else {
      current[addon.id] = {
        ...selection,
        ticketIndices: next,
        count: next.length,
      };
    }
    setSelectedAddons(current);
  };

  return (
    <React.Fragment>
      <div className="event-page__body event-page__body--checkout">
        <div className="checkout__payment">
          <h4 className="checkout__subtitle">
            {user?.name ? user.firstName + ", " : ""}Select your add-ons
          </h4>
          {addons.length > 0 && (
            <React.Fragment>
              <Addons
                addons={addons}
                selectedAddons={selectedAddons}
                ticketOptions={ticketOptions}
                currency={currency}
                handleAddonChange={handleAddonChange}
                handleTicketAssignment={handleTicketAssignment}
                handleAddonCountChange={handleAddonCountChange}
              />
            </React.Fragment>
          )}
        </div>
        <CheckoutDetails
          subtotal={subtotal}
          showReceipt={showReceipt}
          setShowReceipt={setShowReceipt}
          grandTotal={grandTotal}
          event={event}
          tickets={event.type === "full" ? tempCart : tickets}
          selectedAddons={selectedAddons}
          setGrandTotal={setGrandTotal}
          totalTickets={totalTickets}
          cancelOrder={cancelOrder}
          handleStepClick={handleStepClick}
          editGuests={editGuests}
          currency={currency}
          commission={commission}
        />
      </div>
      <CTAPopup
        actionHandler={nextStep}
        title=""
        variation="single"
        text="Payment"
        textIcon={<Arrow className="cta-popup__arrow-icon" />}
        disabled={false}
        expiry={expiry}
        /* tooltipShown={checkoutRenderCount === 1} */
      />
    </React.Fragment>
  );
};

export default CheckOut;
